import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import c from './link-tile.module.scss';
import preview from './assets/preview.png';
import empty from './assets/empty.svg';
import WidgetIcon from '../../widget-library/WidgetIcon';
import { Button, Icon } from '@monash/portal-react';
import IconDisplay from '../../components/icon-display/IconDisplay';

import { WidgetContext } from '@/components/providers/WidgetProvider';
import { backgroundColorOptions } from '../../widget-container/edit-menu/additional-options/colorOptions';
import { useApi, getCustomImage } from '@monash/portal-frontend-common';
import EditLink from './edit-link/EditLink';
import { IMAGE_EFFECTS, IMAGE_EFFECTS_BG_SIZE } from '../../../../../constants';

const LinkTile = ({
  widgetId,
  data,
  updateData,
  escapeSettingActionsFocusRef,
  ctaColor,
  setBackgroundStyles,
}) => {
  const hasCheckedForFileName = useRef(false);
  const link = Array.isArray(data?.link) ? data?.link : [];
  const visible = data?.visible ?? true;
  const linkObject = link[0];
  const editLinkButtonRef = useRef();
  const { setEditActiveWidget, editActiveWidget } = useContext(WidgetContext);

  const [skipInitialCall, setSkipInitialCall] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageEffect, setImageEffect] = useState(IMAGE_EFFECTS.STRETCH_TO_FIT);

  useEffect(() => {
    if (data) {
      // Only on the first time the data has loaded from firestore, we will
      // check if there is a file name. We can't just check the imageFileName
      // on its own, as it will keep changing when images get uploaded.
      if (data.imageFileName && !hasCheckedForFileName.current) {
        setSkipInitialCall(false);
      }
      hasCheckedForFileName.current = true;
    }
  }, [data]);

  const { data: getCustomImageResponse } = useApi({
    requestFn: getCustomImage,
    args: [widgetId],
    skipInitialCall,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  useEffect(() => {
    setBackgroundStyles((currentStyles) => ({
      ...currentStyles,
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: IMAGE_EFFECTS_BG_SIZE[imageEffect],
      filter: imageEffect === IMAGE_EFFECTS.BLUR ? 'blur(6px)' : 'none',
    }));
  }, [imageUrl, imageEffect]);

  useEffect(() => {
    if (getCustomImageResponse) {
      setImageUrl(getCustomImageResponse);
    } else if (data?.backgroundURL) {
      setImageUrl(data?.backgroundURL);
    }
  }, [getCustomImageResponse, data?.backgroundURL]);

  useEffect(() => {
    if (data?.imageEffect) {
      setImageEffect(data?.imageEffect);
    }
  }, [data]);

  return (
    <div className={c.linkTile}>
      {editActiveWidget === widgetId && (
        <EditLink
          widgetId={widgetId}
          data={data}
          updateData={updateData}
          currentLink={linkObject}
          visible={visible}
          escapeFocusRef={escapeSettingActionsFocusRef}
          setImageUrl={setImageUrl}
          imageUrl={imageUrl}
          setImageEffect={setImageEffect}
          imageEffect={imageEffect}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          isLegacyUrl={Boolean(data?.backgroundURL)}
        />
      )}

      {linkObject ? (
        <a
          className={c.link}
          href={linkObject.url}
          target="_blank"
          rel="noreferrer"
          aria-description="Opens in a new window"
          onKeyDown={(e) => e.stopPropagation()}
        >
          {linkObject.icon ? (
            <IconDisplay icon={linkObject.icon} url={linkObject.url} />
          ) : null}

          {visible && (
            <p className={c.linkName} style={{ fontSize: data?.fontSize }}>
              {linkObject.name}
            </p>
          )}
        </a>
      ) : (
        <div className={c.emptyLink} onKeyDown={(e) => e.stopPropagation()}>
          <div className={c.graphic}>
            <img src={empty} alt="" />
            <p>No link yet</p>
          </div>
          <div className={c.editLink}>
            <Button
              ref={editLinkButtonRef}
              variant="text"
              size="small"
              icon={<Icon.Pencil />}
              onClick={() => {
                setEditActiveWidget(widgetId);
              }}
              mode="card"
              style={{ color: ctaColor }}
            >
              Set/Edit link
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const LinkTileModule = {
  component: LinkTile,
  name: 'Link tile',
  icon: WidgetIcon.Shortcuts,
  previewImage: preview,
  description: 'Create a fun, custom quick link to your favourite site.',
  previewBackgroundColor: '#FEE2E2',
  defaultSize: 'S',
  getContainerStyles: (data) => {
    const backgroundColor = data?.backgroundColor || 'var(--card-bg-color)';
    const stretchPosition = data?.stretchPosition || false;
    const imageRepeat = data?.imageRepeat || false;
    const backgroundUrl = data?.backgroundURL;

    const backgroundColorValue = backgroundColorOptions.options.find(
      (opt) => opt.id === backgroundColor
    );
    return {
      color: backgroundColorValue?.text,
      backgroundImage: `url(${backgroundUrl})`,
      backgroundColor: backgroundColorValue?.value || 'var(--card-bg-color)',
      backgroundRepeat: imageRepeat ? 'repeat' : 'no-repeat',
      backgroundSize: stretchPosition ? 'cover' : 'auto',
    };
  },
  additionalOptions: [
    {
      key: 'link',
      editType: null,
      default: [],
    },
  ],
  additionalActions: ({ setEditActiveWidget }) => [
    {
      action: ({ widgetId }) => {
        setEditActiveWidget(widgetId);
      },
      icon: <Icon.Pencil size={20} />,
      text: 'Edit',
      haspopup: 'dialog',
    },
  ],
};

export default LinkTileModule;
